import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { 
  Lock, Building2, Briefcase, FolderKanban, Megaphone, User 
} from 'lucide-react';

const THREAD_TYPES = [
  { 
    value: 'private', 
    label: 'Private Thread', 
    description: 'Invite specific members',
    icon: Lock,
    color: '#8B5CF6'
  },
  { 
    value: 'department', 
    label: 'Department Thread', 
    description: 'Auto-syncs with department members',
    icon: Building2,
    color: '#3B82F6'
  },
  { 
    value: 'project', 
    label: 'Project Thread', 
    description: 'Auto-syncs with project team',
    icon: FolderKanban,
    color: '#10B981'
  },
  { 
    value: 'system', 
    label: 'System Announcement', 
    description: 'Visible to all, admins post only',
    icon: Megaphone,
    color: '#EF4444'
  },
  { 
    value: 'personal', 
    label: 'Personal Notes', 
    description: 'Private to you only',
    icon: User,
    color: '#6B7280'
  }
];

const AUTO_ARCHIVE_OPTIONS = [
  { value: null, label: 'Never' },
  { value: 7, label: '7 days' },
  { value: 14, label: '14 days' },
  { value: 30, label: '30 days' },
  { value: 60, label: '60 days' }
];

const CreateThreadDialog = ({ open, onClose, onCreate, departments, projects }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'private',
    linkedDepartmentId: '',
    linkedProjectId: '',
    autoArchiveAfterDays: null
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) return;

    setLoading(true);
    try {
      const data = {
        title: formData.title.trim(),
        description: formData.description.trim() || null,
        type: formData.type,
        members: [],
        linkedDepartmentId: formData.type === 'department' ? formData.linkedDepartmentId : null,
        linkedRoleId: null,
        linkedProjectId: formData.type === 'project' ? formData.linkedProjectId : null,
        autoMembership: ['department', 'project', 'system'].includes(formData.type),
        autoArchiveAfterDays: formData.autoArchiveAfterDays
      };
      
      await onCreate(data);
      
      // Reset form
      setFormData({
        title: '',
        description: '',
        type: 'private',
        linkedDepartmentId: '',
        linkedProjectId: '',
        autoArchiveAfterDays: null
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      title: '',
      description: '',
      type: 'private',
      linkedDepartmentId: '',
      linkedProjectId: '',
      autoArchiveAfterDays: null
    });
    onClose();
  };

  const selectedType = THREAD_TYPES.find(t => t.value === formData.type);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg" data-testid="create-thread-dialog">
        <DialogHeader>
          <DialogTitle style={{ color: '#264653' }}>Create New Thread</DialogTitle>
          <DialogDescription>
            Start a structured conversation with your team
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 py-4">
          {/* Thread Type Selection */}
          <div className="space-y-3">
            <Label>Thread Type</Label>
            <RadioGroup
              value={formData.type}
              onValueChange={(value) => setFormData(prev => ({ 
                ...prev, 
                type: value,
                linkedDepartmentId: '',
                linkedProjectId: ''
              }))}
              className="grid grid-cols-1 gap-2"
            >
              {THREAD_TYPES.map(type => {
                const Icon = type.icon;
                const isSelected = formData.type === type.value;
                
                return (
                  <div key={type.value}>
                    <RadioGroupItem
                      value={type.value}
                      id={type.value}
                      className="peer sr-only"
                    />
                    <Label
                      htmlFor={type.value}
                      className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        isSelected ? 'shadow-sm' : 'hover:bg-gray-50'
                      }`}
                      style={{ 
                        borderColor: isSelected ? type.color : '#E5E7EB',
                        backgroundColor: isSelected ? `${type.color}08` : 'transparent'
                      }}
                    >
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: `${type.color}15` }}
                      >
                        <Icon className="w-5 h-5" style={{ color: type.color }} />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-sm" style={{ color: '#1F2937' }}>
                          {type.label}
                        </p>
                        <p className="text-xs" style={{ color: '#6B7280' }}>
                          {type.description}
                        </p>
                      </div>
                    </Label>
                  </div>
                );
              })}
            </RadioGroup>
          </div>

          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">Thread Title *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="e.g., Q1 Planning Discussion"
              required
              data-testid="thread-title-input"
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="What is this thread about?"
              rows={2}
              data-testid="thread-desc-input"
            />
          </div>

          {/* Department Selection (for department type) */}
          {formData.type === 'department' && (
            <div className="space-y-2">
              <Label htmlFor="department">Link to Department</Label>
              <Select
                value={formData.linkedDepartmentId}
                onValueChange={(value) => setFormData(prev => ({ ...prev, linkedDepartmentId: value }))}
              >
                <SelectTrigger data-testid="department-select">
                  <SelectValue placeholder="Select a department" />
                </SelectTrigger>
                <SelectContent>
                  {departments.map(dept => (
                    <SelectItem key={dept.id} value={dept.id}>
                      {dept.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Project Selection (for project type) */}
          {formData.type === 'project' && (
            <div className="space-y-2">
              <Label htmlFor="project">Link to Project</Label>
              <Select
                value={formData.linkedProjectId}
                onValueChange={(value) => setFormData(prev => ({ ...prev, linkedProjectId: value }))}
              >
                <SelectTrigger data-testid="project-select">
                  <SelectValue placeholder="Select a project" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map(proj => (
                    <SelectItem key={proj.id} value={proj.id}>
                      {proj.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Auto-Archive Setting */}
          <div className="space-y-2">
            <Label htmlFor="autoArchive">Auto-Archive After Inactivity</Label>
            <Select
              value={formData.autoArchiveAfterDays?.toString() || 'null'}
              onValueChange={(value) => setFormData(prev => ({ 
                ...prev, 
                autoArchiveAfterDays: value === 'null' ? null : parseInt(value) 
              }))}
            >
              <SelectTrigger data-testid="auto-archive-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {AUTO_ARCHIVE_OPTIONS.map(opt => (
                  <SelectItem key={opt.value || 'null'} value={opt.value?.toString() || 'null'}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!formData.title.trim() || loading}
              style={{ backgroundColor: selectedType?.color || '#2A9D8F', color: '#FFFFFF' }}
              data-testid="create-thread-submit"
            >
              {loading ? 'Creating...' : 'Create Thread'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateThreadDialog;
